import { Box, Stack, Typography } from '@mui/material';
import DateInput from 'modules/common/component/form/DateInput';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Report from 'modules/common/component/report';
import { useDialog, useFetch } from 'modules/common/hook';
import Table, { TableOptions } from 'modules/common/component/table';
import { Column } from 'modules/common/component/table/type';
import { BE_DATE_TIME_FORMAT, FE_DATE_FORMAT } from 'modules/common/constants';
import { useTablePagination } from 'modules/common/component/table/useTablePagination';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReportFilterOption } from 'modules/common/component/report/type';
import { Pageable } from 'modules/common/type';
import { formatEndDate, formatStartDate, isValidDate, maxDateTable, NumericFormatText } from 'modules/common/utils';
import Filter from 'modules/common/component/filter';
import { SALE_API } from 'modules/common/service';
import { DEFAULT_VALUES, FILTER_KEY, FormSchema, SELECT_COLUMNS_KEY } from './const';
import { SaleProfitReport } from 'modules/schema/SaleProfitReport';
import { ProfitDetailTableSub } from './ProfitDetailTableSub';
import { RECEIPT_TYPE } from 'modules/common/apiConstants';
import SelectInput from 'modules/common/component/form/SelectInput';
import { map } from 'lodash';

interface Props {}

const COLUMNS: Column<SaleProfitReport>[] = [
  {
    type: 'SUB_ROW',
    allowCollapse: true,
    width: '10px',
    collapseProps: {
      unmountOnExit: true,
    },
    render: (record) => <ProfitDetailTableSub profitReport={record} />,
  },
  { type: 'INDEX', width: '30px', sticky: true },
  {
    title: 'sale.date',
    render: (record) => record.createdTime?.format(FE_DATE_FORMAT),
    selectColumnConfig: {
      disabled: true,
      defaultChecked: true,
    },
  },
  {
    title: 'sellingProduct.title.receiptCode',
    render: (record) => record?.code,
    selectColumnConfig: {
      disabled: true,
      defaultChecked: true,
    },
  },
  {
    title: 'sellingProduct.title.receiptType',
    render: (record) => {
      return (
        record.receiptType && (
          <Typography variant="body1" fontSize="14px">
            <FormattedMessage id={RECEIPT_TYPE[record?.receiptType]?.label} />
          </Typography>
        )
      );
    },
    selectColumnConfig: {
      disabled: true,
      defaultChecked: true,
    },
  },
  {
    title: 'sellingProduct.title.customerName',
    render: 'customerName',
    selectColumnConfig: {
      disabled: true,
      defaultChecked: true,
    },
    enableEllipsis: true,
  },
  {
    title: 'sellingProduct.title.phoneNumber',
    render: 'phoneNumber',
    selectColumnConfig: {
      disabled: false,
      defaultChecked: true,
    },
  },
  {
    title: 'sellingProduct.title.print.paid.amount',
    fieldName: 'amountPaid',
    render: (record) => NumericFormatText(record.amountPaid),
    selectColumnConfig: {
      disabled: false,
      defaultChecked: true,
    },
  },
  {
    title: 'report.profitDetail.table.label.profitAmount',
    fieldName: 'profitTotal',
    render: (record) => NumericFormatText(record.profitTotal),
    selectColumnConfig: {
      disabled: false,
      defaultChecked: true,
    },
  },
];

const ProfitTable: React.FunctionComponent<Props> = () => {
  const [filterReportOptions, setFilterReportOptions] = React.useState<ReportFilterOption[]>([]);
  const [summaryData, setSummaryData] = useState<SaleProfitReport>({});
  const [openPrintDialog, onOpenPrintDialog, onClosePrintDialog] = useDialog();
  const { page, pageSize, paginationProps, resetPage } = useTablePagination();
  const intl = useIntl();
  const form = useForm<FormSchema>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });
  const fromDateOrigin = form.watch('fromDate');
  const toDateOrigin = form.watch('toDate');
  const fromDate = formatStartDate(fromDateOrigin);
  const toDate =
    moment(form.watch('toDate')) < moment()
      ? formatEndDate(moment(form.watch('toDate')).add(-1, 'day'))
      : formatEndDate(moment());
  const allOptions = intl.formatMessage({ id: 'all' });
  const receiptType = form.watch('receiptType');

  const [searchParams, setSearchParams] = React.useState<
    {
      fromDate: string;
      toDate: string;
      saleReceiptCode: string;
    } & Pageable
  >(DEFAULT_VALUES);

  const formatSearchParams = useMemo(() => {
    return {
      ...searchParams,
      receiptTypeList: receiptType ? map(receiptType, 'value') : undefined,
      fromDate,
      toDate,
      page: page,
      pageSize: pageSize,
    };
  }, [fromDate, page, pageSize, searchParams, toDate, receiptType]);

  const { data: reportPage, isValidating } = useFetch(SALE_API.getProfitPaging(formatSearchParams), {
    globalLoading: true,
    revalidateOnFocus: false,
    enabled: isValidDate(fromDate, toDate, 'TABLE'),
  });
  const { data: printDataPage } = useFetch(
    SALE_API.getProfitPaging({ ...formatSearchParams, page: 0, pageSize: 10000 }),
    {
      globalLoading: true,
      revalidateOnFocus: false,
      enabled: openPrintDialog && isValidDate(fromDate, toDate, 'TABLE'),
    },
  );
  const exportUrl = React.useMemo(() => SALE_API.getProfitExcel(formatSearchParams), [formatSearchParams]);

  useEffect(() => {
    if (reportPage && reportPage.summary && Object.keys(reportPage.summary).length > 0 && !isValidating) {
      setSummaryData(reportPage?.summary);
    }
  }, [reportPage, isValidating]);

  useEffect(() => {
    setFilterReportOptions([
      { title: 'fromDate', value: moment(fromDate, BE_DATE_TIME_FORMAT).format(FE_DATE_FORMAT) },
      { title: 'toDate', value: moment(toDate, BE_DATE_TIME_FORMAT).format(FE_DATE_FORMAT) },
      {
        title: 'productLabel',
        value: allOptions,
      },
    ]);
  }, [fromDate, toDate, allOptions]);

  const [selectedColumns, setSelectedColumn] = React.useState<Column<SaleProfitReport>[]>(
    COLUMNS?.filter((column) => !column.selectColumnConfig || column.selectColumnConfig.defaultChecked),
  );
  const TABLE_OPTIONS = useMemo(() => {
    let tableOptions: TableOptions = {
      position: 'top',
      startIndex: COLUMNS.length === selectedColumns.length ? 4 : 0,
      useRender: ['debtAmount'],
      record: summaryData,
    };
    return tableOptions;
  }, [summaryData, selectedColumns.length]);

  return (
    <Stack direction="column" gap={2}>
      <Report.Title
        title="report.profit.label"
        showSubTitle
        foundResultAmount={reportPage?.content.length || 0}
        totalElementAmount={reportPage?.pagination.totalElements}
        renderRight={
          <Stack direction="row" alignItems="center" justifyContent="center" gap="16px" paddingTop={2.5}>
            <Report.SearchInput
              form={form}
              name="saleReceiptCode"
              onSearch={() => {
                setSearchParams((prev) => ({ ...prev, saleReceiptCode: form.getValues('saleReceiptCode') }));
                resetPage();
              }}
            />
          </Stack>
        }
      />
      <Filter
        filters={[]}
        popupWidth="600px"
        onSave={(filterValue, filterConfig) => {
          if (fromDate && toDate) {
            setSearchParams((prev: any) => ({
              ...prev,
            }));
            resetPage();
          }
          setFilterReportOptions([
            { title: 'fromDate', value: moment(fromDate, BE_DATE_TIME_FORMAT).format(FE_DATE_FORMAT) },
            { title: 'toDate', value: moment(toDate, BE_DATE_TIME_FORMAT).format(FE_DATE_FORMAT) },
            {
              title: 'productLabel',
              value:
                filterConfig.MEDICATION?.length === 0
                  ? allOptions
                  : filterConfig.MEDICATION.map((ele) => intl.formatMessage({ id: ele.name })).join(', '),
            },
          ]);
        }}
        localStorageKey={FILTER_KEY}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="column" gap="10px">
            <Stack direction="row" alignItems="center" justifyContent="center" gap="13px">
              <SelectInput
                form={form}
                getValue="value"
                renderLabel="label"
                options={Object.values(RECEIPT_TYPE)}
                name="receiptType"
                label="billLabel"
                placeholder="billLabel"
                multiple
                showCheckBox
              />
              <DateInput
                form={form}
                name="fromDate"
                label="fromDate"
                mode="start"
                required
                format={FE_DATE_FORMAT}
                maxDate={moment() && moment(toDate)}
                type="TABLE"
                onChange={resetPage}
              />
              <DateInput
                form={form}
                name="toDate"
                label="toDate"
                mode="end"
                required
                format={FE_DATE_FORMAT}
                maxDate={maxDateTable(fromDate, fromDateOrigin, toDateOrigin)}
                minDate={moment(fromDate).add(+1, 'day')}
                type="TABLE"
                onChange={resetPage}
              />
              <Box paddingTop={'11px'}>
                <Report.RefreshButton
                  onClick={() => {
                    form.reset();
                    setSearchParams(DEFAULT_VALUES);
                  }}
                />
              </Box>
            </Stack>
            <Filter.View />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" gap="8px">
            <Report.SelectColumnButton
              columns={COLUMNS}
              onSave={setSelectedColumn}
              localStorageKey={SELECT_COLUMNS_KEY}
            />
            <Report.ExportDataButton exportUrl={exportUrl} />
            <Report.PrintDataButton
              title="report.profit.label"
              openDialog={openPrintDialog}
              onOpenDialog={onOpenPrintDialog}
              onCloseDialog={onClosePrintDialog}
              content={
                <Stack direction="column" gap={1}>
                  <Report.FilterPrintFormat filterOptions={filterReportOptions} />
                  <Table
                    data={printDataPage?.content}
                    columns={selectedColumns}
                    dataKey="id"
                    rowProps={{ sx: { pageBreakAfter: 'auto' } }}
                  />
                </Stack>
              }
            />
          </Stack>
        </Stack>
      </Filter>
      <Table
        columns={selectedColumns}
        options={TABLE_OPTIONS}
        dataKey="id"
        data={reportPage?.content}
        paginationProps={paginationProps}
        totalElements={reportPage?.pagination.totalElements}
        stickyHeader
        maxHeigth="100%"
        warningTitle={intl.formatMessage({ id: 'report.disclaim.label' })}
      />
    </Stack>
  );
};

export default ProfitTable;
